import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CartLine, Product } from '../types';
import { getProductById, PRODUCTS } from '../data/products';
interface CartContextValue {
  lines: CartLine[];
  count: number;
  subtotal: number;
  shipping: number;
  discount: number;
  total: number;
  freeShipping: boolean;
  couponCode: string | null;
  addItem: (productId: string, colorId: string, quantity?: number) => void;
  updateQuantity: (lineId: string, quantity: number) => void;
  removeItem: (lineId: string) => void;
  applyCoupon: (code: string) => boolean;
  clearCoupon: () => void;
  lineProduct: (line: CartLine) => Product | undefined;
}
const FREE_SHIPPING_THRESHOLD = 3500;
const SHIPPING_COST = 290;
const CartContext = createContext<CartContextValue | null>(null);
function unitPrice(product: Product): number {
  return product.salePrice ?? product.price;
}
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>([{
    id: 'l1',
    productId: PRODUCTS[0].id,
    colorId: 'pink',
    quantity: 2
  }, {
    id: 'l2',
    productId: PRODUCTS[1].id,
    colorId: 'black',
    quantity: 1
  }]);
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const addItem = useCallback((productId: string, colorId: string, quantity = 1) => {
    setLines(prev => {
      const existing = prev.find(l => l.productId === productId && l.colorId === colorId);
      if (existing) {
        return prev.map(l => l.id === existing.id ? {
          ...l,
          quantity: l.quantity + quantity
        } : l);
      }
      return [...prev, {
        id: `l${Date.now()}`,
        productId,
        colorId,
        quantity
      }];
    });
  }, []);
  const updateQuantity = useCallback((lineId: string, quantity: number) => {
    setLines(prev => prev.map(l => l.id === lineId ? {
      ...l,
      quantity: Math.max(1, quantity)
    } : l));
  }, []);
  const removeItem = useCallback((lineId: string) => {
    setLines(prev => prev.filter(l => l.id !== lineId));
  }, []);
  const applyCoupon = useCallback((code: string) => {
    if (code.trim().toUpperCase() === 'ZADAR10') {
      setCouponCode('ZADAR10');
      return true;
    }
    return false;
  }, []);
  const clearCoupon = useCallback(() => setCouponCode(null), []);
  const value = useMemo<CartContextValue>(() => {
    const subtotal = lines.reduce((sum, line) => {
      const product = getProductById(line.productId);
      return product ? sum + unitPrice(product) * line.quantity : sum;
    }, 0);
    const count = lines.reduce((sum, l) => sum + l.quantity, 0);
    const freeShipping = subtotal >= FREE_SHIPPING_THRESHOLD || subtotal === 0;
    const shipping = freeShipping ? 0 : SHIPPING_COST;
    const discount = couponCode ? Math.round(subtotal * 0.1) : 0;
    return {
      lines,
      count,
      subtotal,
      shipping,
      discount,
      total: subtotal + shipping - discount,
      freeShipping,
      couponCode,
      addItem,
      updateQuantity,
      removeItem,
      applyCoupon,
      clearCoupon,
      lineProduct: (line: CartLine) => getProductById(line.productId)
    };
  }, [lines, couponCode, addItem, updateQuantity, removeItem, applyCoupon, clearCoupon]);
  return <CartContext.Provider value={value} data-id="element-716" data-mp="25fij2l62nagc">{children}</CartContext.Provider>;
}
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
}
export { FREE_SHIPPING_THRESHOLD, SHIPPING_COST };